import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

type AuthMode = 'login' | 'register';

export function VibeLoginModal(): ReactElement | null {
  const {
    loginOpen,
    loginContext,
    closeLogin,
    loginWithEmail,
    registerWithEmail,
    loginWithOAuth,
    error,
    clearError,
  } = useAuth();

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setName('');
    setConfirmPassword('');
    setValidationError(null);
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (!loginOpen) {
      resetForm();
      setMode('login');
    }
  }, [loginOpen, resetForm]);

  if (!loginOpen) {
    return null;
  }

  const validate = (): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      setValidationError('Enter a valid email address');
      return false;
    }
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return false;
    }
    if (mode === 'register') {
      if (!name.trim()) {
        setValidationError('Name is required');
        return false;
      }
      if (password !== confirmPassword) {
        setValidationError('Passwords do not match');
        return false;
      }
    }
    setValidationError(null);
    return true;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (submittingRef.current || isLoading) return;
    if (!validate()) return;
    submittingRef.current = true;
    setIsLoading(true);
    try {
      if (mode === 'login') {
        await loginWithEmail({ email, password });
      } else {
        await registerWithEmail({ email, password, name: name.trim() });
      }
    } catch {
      /* error surfaced via context */
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const contextLabel = loginContext ? ` ${loginContext}` : '';

  return createPortal(
    <div className="vibe-auth-modal" role="dialog" aria-modal="true" aria-labelledby="vibe-auth-title">
      <button type="button" className="vibe-auth-modal__backdrop" aria-label="Close" onClick={closeLogin} />
      <div className="vibe-auth-modal__panel">
        <button type="button" className="vibe-auth-modal__close" aria-label="Close" onClick={closeLogin}>
          <X className="vibe-sidebar__icon-sm" aria-hidden />
        </button>
        <h2 id="vibe-auth-title" className="vibe-auth-modal__title">
          Sign in{contextLabel}
        </h2>
        <p className="vibe-auth-modal__subtitle">Use your Landi account to generate and preview sites.</p>

        <div className="vibe-auth-modal__oauth">
          <button
            type="button"
            className="vibe-auth-modal__oauth-btn"
            disabled={isLoading}
            onClick={() => void loginWithOAuth('google')}
          >
            Continue with Google
          </button>
          <button
            type="button"
            className="vibe-auth-modal__oauth-btn"
            disabled={isLoading}
            onClick={() => void loginWithOAuth('github')}
          >
            Continue with GitHub
          </button>
        </div>

        <div className="vibe-auth-modal__divider">
          <span>or</span>
        </div>

        <form className="vibe-auth-modal__form" onSubmit={(e) => void onSubmit(e)}>
          {mode === 'register' ? (
            <label className="vibe-auth-modal__field">
              <span>Name</span>
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </label>
          ) : null}
          <label className="vibe-auth-modal__field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </label>
          <label className="vibe-auth-modal__field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </label>
          {mode === 'register' ? (
            <label className="vibe-auth-modal__field">
              <span>Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
            </label>
          ) : null}

          {validationError ? <p className="vibe-auth-modal__error">{validationError}</p> : null}
          {error ? <p className="vibe-auth-modal__error">{error}</p> : null}

          <button type="submit" className="vibe-auth-modal__submit" disabled={isLoading}>
            {isLoading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <p className="vibe-auth-modal__switch">
          {mode === 'login' ? (
            <>
              No account?{' '}
              <button type="button" onClick={() => { setMode('register'); setValidationError(null); clearError(); }}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => { setMode('login'); setValidationError(null); clearError(); }}>
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>,
    document.body,
  );
}
